import React, { useState, useEffect } from "react";
import Card from "components/card";
import {
  MdAdd,
  MdDelete,
  MdPerson,
  MdPhone,
  MdEdit,
  MdClose,
  MdSave,
  MdSms,
  MdUpload,
  MdBadge,
  MdCheckCircle,
  MdInsertDriveFile,
  MdGroup,
  MdManageAccounts,
} from "react-icons/md";
import { toast, Toaster } from "react-hot-toast";
import { tokenManager } from "utils/tokenManager";
import * as XLSX from "xlsx";

const API_BASE = "https://usibras-web-backend.onrender.com/api/shift-sms/recipients";
const PASS_API_BASE = `${process.env.REACT_APP_PASS_API_BASE || "https://usibras-web-backend.onrender.com"}/api/passes`;

const REQUIRED_COLUMNS = [
  "Name",
  "ID",
  "Department",
  "Date",
  "Time",
  "Verification Method",
  "Attendance Check Point",
];

// Returns a promise that resolves to null (valid) or an error message string
const validateFileColumns = (file) =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const workbook = XLSX.read(e.target.result, { type: "array" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });
        const headers = (rows[0] || []).map((h) => String(h).trim());
        const missing = REQUIRED_COLUMNS.filter((col) => !headers.includes(col));
        if (missing.length > 0) {
          resolve(`Missing columns: ${missing.join(", ")}`);
        } else {
          resolve(null);
        }
      } catch {
        resolve("Could not read file. Make sure it is a valid .xlsx or .xls file.");
      }
    };
    reader.onerror = () => resolve("Failed to read file.");
    reader.readAsArrayBuffer(file);
  });

// Normalize API shape → component shape
const normalize = (r) => ({
  id: r.id,
  staffId: r.staff_id,
  fullName: r.full_name,
  contact: r.contact,
  role: r.role,
  type: r.type ?? null,
  roleType: r.role_type ?? null,
  department: r.department ?? null,
});

const EMPTY_FORM = {
  staffId: "",
  fullName: "",
  contact: "",
  role: "",
  roleType: "",
  department: "",
};

const ROLE_OPTIONS = ["Manager", "Staff"];
const ROLE_TYPE_OPTIONS = ["Normal Staff", "Supervisor"];
const DEPARTMENT_OPTIONS = ["Sheeling", "Manual Peeling"];

// ─── Role badge colours ──────────────────────────────────────────────────────
const roleBadge = (role) =>
  role === "Manager"
    ? "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"
    : "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300";

const typeBadge = (type) =>
  type === "Commercial"
    ? "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
    : "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300";

const roleTypeBadge = (roleType) =>
  roleType === "Supervisor"
    ? "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300"
    : "bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300";

const computePassSummary = (items = []) => {
  let activePasses = 0;
  let endedPasses = 0;

  items.forEach((item) => {
    const status = String(item?.status || item?.effective_status || "").trim();
    if (status === "Granted") activePasses += 1;
    if (status === "Expired") endedPasses += 1;
  });

  return { active_passes: activePasses, ended_passes: endedPasses };
};

// ────────────────────────────────────────────────────────────────────────────

const ShiftSMS = () => {
  const [recipients, setRecipients] = useState([]);
  const [summary, setSummary] = useState({ managers: 0, staff: 0, commercial: 0, shift: 0, normal_staff: 0, supervisors: 0, active_passes: 0, ended_passes: 0 });
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const pageSize = 10;

  const getHeaders = () => {
    const auth = tokenManager.getAuthHeader();
    return { "Content-Type": "application/json", ...(auth ? { Authorization: auth } : {}) };
  };

  const fetchRecipients = async () => {
    setLoading(true);
    try {
      const res = await fetch(API_BASE, { headers: getHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to load recipients");
      setRecipients((data.data || []).map(normalize));
      if (data.summary) {
        setSummary((prev) => ({
          ...prev,
          managers: data.summary.managers ?? 0,
          staff: data.summary.staff ?? 0,
          commercial: data.summary.commercial ?? 0,
          shift: data.summary.shift ?? 0,
          normal_staff: data.summary.normal_staff ?? 0,
          supervisors: data.summary.supervisors ?? 0,
        }));
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchPassCounts = async () => {
    try {
      const res = await fetch(PASS_API_BASE, { headers: getHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || data?.message || "Failed to load pass statistics");
      const items = Array.isArray(data?.data) ? data.data : [];
      const passSummary = computePassSummary(items);
      setSummary((prev) => ({
        ...prev,
        active_passes: passSummary.active_passes,
        ended_passes: passSummary.ended_passes,
      }));
    } catch (err) {
      console.warn(err.message || "Could not load pass statistics");
    }
  };

  useEffect(() => { fetchRecipients(); fetchPassCounts(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setCurrentPage(1);
  }, [recipients.length, searchTerm]);

  // ── form helpers ──────────────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "role" && value !== "Staff" ? { roleType: "", department: "" } : {}),
      ...(name === "roleType" ? { department: "" } : {}),
    }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const errs = {};
    if (!form.staffId.trim()) errs.staffId = "Staff ID is required";
    if (!form.fullName.trim()) errs.fullName = "Full name is required";
    if (!form.contact.trim()) errs.contact = "Contact number is required";
    if (!form.role) errs.role = "Role is required";
    if (form.role === "Staff" && !form.roleType) errs.roleType = "Role Type is required for Staff";
    if (form.role === "Staff" && form.roleType && !form.department) {
      errs.department = "Department is required for staff recipients";
    }
    return errs;
  };

  const openAddForm = () => {
    setForm(EMPTY_FORM);
    setErrors({});
    setEditingId(null);
    setShowForm(true);
  };

  const openEditForm = (recipient) => {
    setForm({
      staffId: recipient.staffId || "",
      fullName: recipient.fullName,
      contact: recipient.contact,
      role: recipient.role,
      roleType: recipient.roleType || "",
      department: recipient.department || "",
    });
    setErrors({});
    setEditingId(recipient.id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }

    const body = {
      staff_id: form.staffId,
      full_name: form.fullName,
      contact: form.contact,
      role: form.role,
      ...(form.role === "Staff" ? {
        role_type: form.roleType,
        ...(form.roleType ? { department: form.department } : {}),
      } : {}),
    };

    const isEdit = editingId !== null;
    const url = isEdit ? `${API_BASE}/${editingId}` : API_BASE;
    const method = isEdit ? "PUT" : "POST";

    const toastId = toast.loading(isEdit ? "Updating recipient…" : "Adding recipient…");
    try {
      const res = await fetch(url, { method, headers: getHeaders(), body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Request failed");
      toast.success(isEdit ? "Recipient updated" : "Recipient added", { id: toastId });
      await fetchRecipients();
      setForm(EMPTY_FORM);
      setErrors({});
      setEditingId(null);
      setShowForm(false);
    } catch (err) {
      toast.error(err.message, { id: toastId });
    }
  };

  const handleDelete = async (id) => {
    const toastId = toast.loading("Deleting recipient…");
    try {
      const res = await fetch(`${API_BASE}/${id}`, { method: "DELETE", headers: getHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Delete failed");
      toast.success("Recipient removed", { id: toastId });
      await fetchRecipients();
    } catch (err) {
      toast.error(err.message, { id: toastId });
    }
  };

  // ── upload helpers ────────────────────────────────────────────────────────
  const acceptFile = async (file) => {
    if (!file) return;
    if (!file.name.endsWith(".xlsx") && !file.name.endsWith(".xls")) {
      toast.error("Please upload an Excel file (.xlsx or .xls)");
      return;
    }
    const validationError = await validateFileColumns(file);
    if (validationError) {
      toast.error(validationError, { duration: 6000 });
      return;
    }
    setUploadFile(file);
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    acceptFile(e.dataTransfer.files[0]);
  };

  const handleFileSelect = (e) => {
    acceptFile(e.target.files[0]);
  };

  const handleUpload = async () => {
    if (!uploadFile) {
      toast.error("Please select a file first");
      return;
    }
    setUploading(true);
    setUploadResult(null);
    const toastId = toast.loading("Uploading & processing attendance…");
    try {
      const auth = tokenManager.getAuthHeader();
      const formData = new FormData();
      formData.append("file", uploadFile);
      const res = await fetch("https://usibras-web-backend.onrender.com/api/shift-sms/upload-attendance", {
        method: "POST",
        headers: auth ? { Authorization: auth } : {},
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Upload failed");
      toast.success("Attendance uploaded — SMS sent", { id: toastId });
      setUploadResult(data);
      setUploadFile(null);
    } catch (err) {
      toast.error(err.message, { id: toastId });
    } finally {
      setUploading(false);
    }
  };

  const filteredRecipients = recipients.filter((r) => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return true;

    return [r.fullName, r.staffId, r.department, r.role, r.roleType]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(query));
  });

  const totalPages = Math.max(1, Math.ceil(filteredRecipients.length / pageSize));
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedRecipients = filteredRecipients.slice(startIndex, startIndex + pageSize);

  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div className="mt-5 h-full">
      <Toaster position="top-right" />

      {/* ── Page header ── */}
      <div className="mb-6 flex flex-col gap-1">
        <div className="flex items-center gap-3">
          <MdSms className="h-8 w-8 text-brand-500" />
          <h2 className="text-2xl font-bold text-navy-700 dark:text-white">
            Shift SMS
          </h2>
          <span className="rounded-full bg-brand-500 px-3 py-1 text-xs font-semibold text-white">
          {recipients.length} recipients
          </span>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Manage SMS recipients and send shift notifications
        </p>
      </div>

      {/* ── Row 1: Summary cards ── */}
      <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Card extra="p-5 flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30">
            <MdManageAccounts className="h-6 w-6 text-blue-500" />
          </div>
          <div>
            <p className="text-2xl font-bold text-blue-500">
              {summary.managers}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Managers</p>
          </div>
        </Card>
        <Card extra="p-5 flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
            <MdGroup className="h-6 w-6 text-green-500" />
          </div>
          <div>
            <p className="text-2xl font-bold text-green-500">
              {summary.staff}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Staff</p>
          </div>
        </Card>
        <Card extra="p-5 flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30">
            <MdBadge className="h-6 w-6 text-amber-500" />
          </div>
          <div>
            <p className="text-2xl font-bold text-amber-500">
              {summary.active_passes}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Active Pass</p>
          </div>
        </Card>
        <Card extra="p-5 flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-purple-100 dark:bg-purple-900/30">
            <MdSms className="h-6 w-6 text-purple-500" />
          </div>
          <div>
            <p className="text-2xl font-bold text-purple-500">
              {summary.ended_passes}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Ended Pass</p>
          </div>
        </Card>
      </div>

      {/* ── Row 2: Recipients table ── */}
      <Card extra="p-0 overflow-hidden mb-6">
        <div className="flex flex-col gap-3 border-b border-gray-100 px-6 py-4 dark:border-navy-700 md:flex-row md:items-center md:justify-between">
          <h3 className="text-base font-semibold text-navy-700 dark:text-white">
            Recipients
          </h3>
          <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center md:w-auto">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, staff ID, or department"
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 placeholder:text-gray-400 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-800 dark:text-white md:w-72"
            />
            <button
              onClick={openAddForm}
              className="flex items-center justify-center gap-1.5 rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-600"
            >
              <MdAdd className="h-4 w-4" />
              Add Recipient
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100 dark:divide-navy-700">
            <thead className="bg-gray-50 dark:bg-navy-800">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">#</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Staff ID</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Contact</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Role</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Role Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Department</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Type</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white dark:divide-navy-700 dark:bg-navy-700">
              {loading ? (
                <tr>
                  <td colSpan={9} className="py-12 text-center text-gray-400 dark:text-gray-500">
                    Loading recipients…
                  </td>
                </tr>
              ) : filteredRecipients.length === 0 ? (
                <tr>
                  <td colSpan={9} className="py-12 text-center text-gray-400 dark:text-gray-500">
                    No matching recipients found.
                  </td>
                </tr>
              ) : (
                paginatedRecipients.map((r, idx) => (
                  <tr key={r.id} className="transition-colors hover:bg-gray-50 dark:hover:bg-navy-600">
                    <td className="px-4 py-3 text-sm text-gray-400 dark:text-gray-500">{startIndex + idx + 1}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-2 py-0.5 text-xs font-mono font-semibold text-gray-600 dark:bg-navy-600 dark:text-gray-300">
                        <MdBadge className="h-3.5 w-3.5" />
                        {r.staffId || "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-100 dark:bg-brand-900/30">
                          <MdPerson className="h-4 w-4 text-brand-500" />
                        </div>
                        <span className="text-sm font-medium text-navy-700 dark:text-white">{r.fullName}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300">
                        <MdPhone className="h-4 w-4 text-gray-400" />
                        {r.contact}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${roleBadge(r.role)}`}>
                        {r.role}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {r.roleType ? (
                        <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${roleTypeBadge(r.roleType)}`}>
                          {r.roleType}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {r.department ? (
                        <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-semibold text-gray-600 dark:bg-navy-600 dark:text-gray-300">
                          {r.department}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {r.type ? (
                        <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${typeBadge(r.type)}`}>
                          {r.type}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openEditForm(r)}
                          className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 hover:text-brand-500 dark:hover:bg-navy-600"
                          title="Edit"
                        >
                          <MdEdit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(r.id)}
                          className="rounded-lg p-1.5 text-gray-500 hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-900/20"
                          title="Delete"
                        >
                          <MdDelete className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {filteredRecipients.length > 0 && (
          <div className="flex items-center justify-between border-t border-gray-100 px-6 py-4 dark:border-navy-700">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Showing {Math.min(startIndex + 1, filteredRecipients.length)}-{Math.min(startIndex + pageSize, filteredRecipients.length)} of {filteredRecipients.length}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-600 disabled:cursor-not-allowed disabled:opacity-40 dark:border-navy-600 dark:text-gray-300"
              >
                Prev
              </button>
              <span className="text-sm text-gray-600 dark:text-gray-300">
                Page {currentPage} / {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-600 disabled:cursor-not-allowed disabled:opacity-40 dark:border-navy-600 dark:text-gray-300"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </Card>

      {/* ── Row 3: Upload attendance data ── */}
      <Card extra="p-6">
        <div className="flex items-center gap-2 mb-6">
          <MdUpload className="h-5 w-5 text-brand-500" />
          <h3 className="text-base font-semibold text-navy-700 dark:text-white">
            Upload Attendance Data
          </h3>
        </div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {/* Drop zone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleFileDrop}
            className={`flex flex-col items-center justify-center rounded-xl border-2 border-dashed px-6 py-10 text-center transition-colors ${
              isDragging
                ? "border-brand-500 bg-brand-50 dark:bg-brand-900/10"
                : uploadFile
                ? "border-green-400 bg-green-50 dark:bg-green-900/10"
                : "border-gray-300 bg-gray-50 hover:border-brand-400 dark:border-navy-600 dark:bg-navy-800"
            }`}
          >
            {uploadFile ? (
              <>
                <MdCheckCircle className="mb-2 h-12 w-12 text-green-500" />
                <p className="text-sm font-semibold text-green-600 dark:text-green-400">{uploadFile.name}</p>
                <p className="mt-0.5 text-xs text-gray-400">{(uploadFile.size / 1024).toFixed(1)} KB</p>
                <button
                  type="button"
                  onClick={() => setUploadFile(null)}
                  className="mt-3 text-xs text-gray-400 underline hover:text-red-500"
                >
                  Remove
                </button>
              </>
            ) : (
              <>
                <MdInsertDriveFile className="mb-3 h-12 w-12 text-gray-300 dark:text-gray-600" />
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Drag &amp; drop your Excel file here
                </p>
                <p className="mt-1 text-xs text-gray-400">.xlsx or .xls only</p>
                <label className="mt-4 cursor-pointer rounded-lg border border-brand-500 px-5 py-2 text-xs font-semibold text-brand-500 hover:bg-brand-50 dark:hover:bg-brand-900/20">
                  Browse File
                  <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleFileSelect} />
                </label>
              </>
            )}
          </div>

          {/* Info + action */}
          <div className="flex flex-col justify-between gap-4">
            <div className="rounded-lg bg-amber-50 p-4 dark:bg-amber-900/10">
              <p className="mb-1 text-xs font-semibold text-amber-700 dark:text-amber-400">Expected columns</p>
              <p className="font-mono text-xs text-amber-600 dark:text-amber-500">
                Name · ID · Department · Date · Time · Verification Method · Attendance Check Point
              </p>
            </div>
            <div className="rounded-lg bg-gray-50 p-4 dark:bg-navy-800 text-sm text-gray-500 dark:text-gray-400 space-y-1">
              <p>1. Upload the attendance <strong>.xlsx</strong> file</p>
              <p>2. Backend processes absences</p>
              <p>3. SMS is sent automatically to relevant recipients</p>
            </div>
            <button
              onClick={handleUpload}
              disabled={!uploadFile || uploading}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand-500 py-3 text-sm font-semibold text-white transition-colors hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <MdUpload className="h-4 w-4" />
              {uploading ? "Processing…" : "Upload & Send SMS"}
            </button>
          </div>
        </div>

        {/* ── Upload result ── */}
        {uploadResult && (
          <div className="mt-6 rounded-xl border border-green-200 bg-green-50 p-5 dark:border-green-800 dark:bg-green-900/10">
            <div className="mb-3 flex items-center gap-2">
              <MdCheckCircle className="h-5 w-5 text-green-500" />
              <p className="text-sm font-semibold text-green-700 dark:text-green-400">
                Report Date: {uploadResult.report_date}
              </p>
            </div>

            {/* Summary counts */}
            <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {[
                { label: "Commercial Total", value: uploadResult.summary?.commercial_total ?? "—" },
                { label: "Commercial < 9 hrs", value: uploadResult.summary?.commercial_below_9hrs ?? "—", warn: true },
                { label: "Shift Total", value: uploadResult.summary?.shift_total ?? "—" },
                { label: "Shift < 8 hrs", value: uploadResult.summary?.shift_below_8hrs ?? "—", warn: true },
              ].map(({ label, value, warn }) => (
                <div key={label} className="rounded-lg bg-white p-3 text-center shadow-sm dark:bg-navy-800">
                  <p className={`text-xl font-bold ${warn ? "text-red-500" : "text-navy-700 dark:text-white"}`}>{value}</p>
                  <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{label}</p>
                </div>
              ))}
            </div>

            {/* Manager message */}
            {uploadResult.manager_message_sent && (
              <div className="mb-4 rounded-lg bg-white px-4 py-3 dark:bg-navy-800">
                <p className="mb-1 text-xs font-semibold text-gray-500 dark:text-gray-400">Manager SMS Sent</p>
                <p className="text-sm text-navy-700 dark:text-white">{uploadResult.manager_message_sent}</p>
              </div>
            )}

            {/* Notified */}
            <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {uploadResult.sms_results?.managers_notified?.length > 0 && (
                <div className="rounded-lg bg-white p-3 dark:bg-navy-800">
                  <p className="mb-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Managers Notified</p>
                  <ul className="space-y-1">
                    {uploadResult.sms_results.managers_notified.map((name) => (
                      <li key={name} className="flex items-center gap-1.5 text-sm text-navy-700 dark:text-white">
                        <MdCheckCircle className="h-3.5 w-3.5 flex-shrink-0 text-green-500" />
                        {name}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {uploadResult.sms_results?.staff_notified?.length > 0 && (
                <div className="rounded-lg bg-white p-3 dark:bg-navy-800">
                  <p className="mb-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Staff Notified</p>
                  <ul className="space-y-1">
                    {uploadResult.sms_results.staff_notified.map((s) => (
                      <li key={s.name} className="flex items-center justify-between text-sm">
                        <span className="flex items-center gap-1.5 text-navy-700 dark:text-white">
                          <MdCheckCircle className="h-3.5 w-3.5 flex-shrink-0 text-green-500" />
                          {s.name}
                        </span>
                        <span className="text-xs text-red-500">{s.hours}h</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Errors */}
            {uploadResult.sms_results?.errors?.length > 0 && (
              <div className="mb-4 rounded-lg bg-red-50 p-3 dark:bg-red-900/10">
                <p className="mb-1 text-xs font-semibold text-red-600 dark:text-red-400">SMS Errors</p>
                <ul className="space-y-0.5">
                  {uploadResult.sms_results.errors.map((err, i) => (
                    <li key={i} className="text-xs text-red-500">{err}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Unmatched IDs */}
            {uploadResult.unmatched_ids?.length > 0 && (
              <div className="rounded-lg bg-amber-50 p-3 dark:bg-amber-900/10">
                <p className="mb-1 text-xs font-semibold text-amber-700 dark:text-amber-400">
                  Unmatched IDs ({uploadResult.unmatched_ids.length}) — not in recipients list
                </p>
                <p className="font-mono text-xs text-amber-600 dark:text-amber-500">
                  {uploadResult.unmatched_ids.join(" · ")}
                </p>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* ── Add / Edit modal ── */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-2xl dark:bg-navy-700">
            {/* Modal header */}
            <div className="mb-6 flex items-center justify-between">
              <h3 className="text-lg font-bold text-navy-700 dark:text-white">
                {editingId !== null ? "Edit Recipient" : "Add Recipient"}
              </h3>
              <button
                onClick={() => setShowForm(false)}
                className="rounded-full p-1.5 text-gray-400 hover:bg-gray-100 dark:hover:bg-navy-600"
              >
                <MdClose className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Staff ID */}
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Staff ID <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <MdBadge className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    name="staffId"
                    value={form.staffId}
                    onChange={handleChange}
                    placeholder="e.g. STF-042"
                    className={`w-full rounded-lg border pl-9 pr-4 py-2.5 text-sm text-gray-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:bg-navy-800 dark:text-white ${
                      errors.staffId ? "border-red-400" : "border-gray-300 dark:border-navy-600"
                    }`}
                  />
                </div>
                {errors.staffId && <p className="mt-1 text-xs text-red-500">{errors.staffId}</p>}
              </div>

              {/* Full Name */}
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <MdPerson className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    name="fullName"
                    value={form.fullName}
                    onChange={handleChange}
                    placeholder="e.g. Kwame Asante"
                    className={`w-full rounded-lg border pl-9 pr-4 py-2.5 text-sm text-gray-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:bg-navy-800 dark:text-white ${
                      errors.fullName ? "border-red-400" : "border-gray-300 dark:border-navy-600"
                    }`}
                  />
                </div>
                {errors.fullName && <p className="mt-1 text-xs text-red-500">{errors.fullName}</p>}
              </div>

              {/* Contact */}
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Contact Number <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <MdPhone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="tel"
                    name="contact"
                    value={form.contact}
                    onChange={handleChange}
                    placeholder="e.g. +233244123456"
                    className={`w-full rounded-lg border pl-9 pr-4 py-2.5 text-sm text-gray-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:bg-navy-800 dark:text-white ${
                      errors.contact ? "border-red-400" : "border-gray-300 dark:border-navy-600"
                    }`}
                  />
                </div>
                {errors.contact && <p className="mt-1 text-xs text-red-500">{errors.contact}</p>}
              </div>

              {/* Role */}
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Role <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-3">
                  {ROLE_OPTIONS.map((role) => (
                    <button
                      key={role}
                      type="button"
                      onClick={() => handleChange({ target: { name: "role", value: role } })}
                      className={`flex-1 rounded-lg border py-2.5 text-sm font-semibold transition-colors ${
                        form.role === role
                          ? "border-brand-500 bg-brand-500 text-white"
                          : "border-gray-300 bg-white text-gray-600 hover:border-brand-400 dark:border-navy-600 dark:bg-navy-800 dark:text-gray-300"
                      }`}
                    >
                      {role}
                    </button>
                  ))}
                </div>
                {errors.role && <p className="mt-1 text-xs text-red-500">{errors.role}</p>}
              </div>

              {/* Role Type — only shown when Staff is selected */}
              {form.role === "Staff" && (
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Role Type <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-3">
                    {ROLE_TYPE_OPTIONS.map((rt) => (
                      <button
                        key={rt}
                        type="button"
                        onClick={() => handleChange({ target: { name: "roleType", value: rt } })}
                        className={`flex-1 rounded-lg border py-2.5 text-sm font-semibold transition-colors ${
                          form.roleType === rt
                            ? "border-brand-500 bg-brand-500 text-white"
                            : "border-gray-300 bg-white text-gray-600 hover:border-brand-400 dark:border-navy-600 dark:bg-navy-800 dark:text-gray-300"
                        }`}
                      >
                        {rt}
                      </button>
                    ))}
                  </div>
                  {errors.roleType && <p className="mt-1 text-xs text-red-500">{errors.roleType}</p>}
                </div>
              )}

              {/* Department — shown when a staff role type is selected */}
              {form.role === "Staff" && form.roleType && (
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Department <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="department"
                    value={form.department}
                    onChange={handleChange}
                    className={`w-full rounded-lg border px-4 py-2.5 text-sm text-gray-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:bg-navy-800 dark:text-white ${
                      errors.department ? "border-red-400" : "border-gray-300 dark:border-navy-600"
                    }`}
                  >
                    <option value="">Select department…</option>
                    {DEPARTMENT_OPTIONS.map((d) => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                  {errors.department && <p className="mt-1 text-xs text-red-500">{errors.department}</p>}
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="flex-1 rounded-lg border border-gray-300 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50 dark:border-navy-600 dark:text-gray-300 dark:hover:bg-navy-600"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-brand-500 py-2.5 text-sm font-semibold text-white hover:bg-brand-600"
                >
                  <MdSave className="h-4 w-4" />
                  {editingId !== null ? "Save Changes" : "Add Recipient"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShiftSMS;
